import { Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import { faker } from '@faker-js/faker';
import { HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import {
  AssignableRole,
  CreateTenantUserDto,
  TenantUserDetail,
  TenantUserStatus,
  UpdateTenantUserDto,
  UpdateTenantUserStatusDto,
} from '@app/core/models/admin-tenant-user.interface';

interface StoredUser extends TenantUserDetail {
  deletedAt?: string;
}

@Injectable({ providedIn: 'root' })
export class AdminTenantUserMocksService {
  private storageKey(tenantId: number): string {
    return `mock_tenant_users_${tenantId}`;
  }

  private loadUsers(tenantId: number): StoredUser[] {
    try {
      const stored = localStorage.getItem(this.storageKey(tenantId));
      if (stored) return JSON.parse(stored);
    } catch {
      // ignore
    }
    return [];
  }

  private saveUsers(tenantId: number, users: StoredUser[]): void {
    try {
      localStorage.setItem(this.storageKey(tenantId), JSON.stringify(users));
    } catch {
      // ignore
    }
  }

  private ensureUsers(tenantId: number): StoredUser[] {
    let users = this.loadUsers(tenantId);
    if (users.length === 0) {
      users = this.generateMockUsers(tenantId);
      this.saveUsers(tenantId, users);
    }
    return users;
  }

  private generateMockUsers(_tenantId: number): StoredUser[] {
    const statuses: TenantUserStatus[] = ['ACTIVE', 'INACTIVE', 'FORCE_CHANGE_PASSWORD'];
    const roles: AssignableRole[] = [
      'tenant_admin',
      'tenant_admin',
      'workshop_manager',
      'workshop_manager',
      'workshop_manager',
    ];

    return Array.from({ length: 5 }, (_, i) => {
      const createdAt = faker.date.past({ years: 2 }).toISOString();
      return {
        userId: faker.string.uuid(),
        email: faker.internet.email(),
        name: faker.person.fullName(),
        role: roles[i],
        status: i < 3 ? 'ACTIVE' : faker.helpers.arrayElement(statuses),
        createdAt,
        updatedAt: faker.date.recent({ days: 30 }).toISOString(),
      } as StoredUser;
    });
  }

  handleMockRequest(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    const urlParts = req.url.split('/');
    const adminIndex = urlParts.indexOf('admin');
    // segments after 'admin': ['tenants', ':tenantId', 'users'] or ['tenants', ':tenantId', 'users', ':userId'] or ['tenants', ':tenantId', 'users', ':userId', 'status']
    const afterAdmin = urlParts.slice(adminIndex + 1);
    const tenantIdStr = afterAdmin[1];
    const tenantId = parseInt(tenantIdStr, 10);
    const userId = afterAdmin[3];
    const isStatusEndpoint = afterAdmin[4] === 'status';

    if (req.method === 'GET' && !userId) {
      return this.listUsers(tenantId, req);
    } else if (req.method === 'POST' && !userId) {
      return this.createUser(tenantId, req.body as CreateTenantUserDto);
    } else if (req.method === 'PUT' && userId && !isStatusEndpoint) {
      return this.updateUser(tenantId, userId, req.body as UpdateTenantUserDto);
    } else if (req.method === 'PATCH' && userId && isStatusEndpoint) {
      return this.updateUserStatus(tenantId, userId, req.body as UpdateTenantUserStatusDto);
    } else if (req.method === 'DELETE' && userId) {
      return this.deleteUser(tenantId, userId);
    }

    throw new Error(`Unsupported mock tenant-user request: ${req.method} ${req.url}`);
  }

  private listUsers(tenantId: number, req: HttpRequest<any>): Observable<HttpEvent<any>> {
    const url = new URL(req.url);
    const page = parseInt(url.searchParams.get('page') || '1', 10);
    const limit = parseInt(url.searchParams.get('limit') || '10', 10);
    const q = url.searchParams.get('q') || '';
    const status = url.searchParams.get('status') as TenantUserStatus | null;

    const allUsers = this.ensureUsers(tenantId);
    const filtered = allUsers.filter((u) => {
      if (u.deletedAt) return false;
      const matchesQuery =
        !q ||
        (u.email && u.email.toLowerCase().includes(q.toLowerCase())) ||
        (u.name && u.name.toLowerCase().includes(q.toLowerCase()));
      const matchesStatus = !status || u.status === status;
      return matchesQuery && matchesStatus;
    });

    const total = filtered.length;
    const data = filtered.slice((page - 1) * limit, page * limit);

    return this.respond({ data, total, page, limit, totalPages: Math.ceil(total / limit) }, 200);
  }

  private createUser(tenantId: number, dto: CreateTenantUserDto): Observable<HttpEvent<any>> {
    const users = this.ensureUsers(tenantId);
    const newUser: StoredUser = {
      userId: faker.string.uuid(),
      email: dto.email,
      name: dto.name ?? null,
      role: dto.role,
      status: 'FORCE_CHANGE_PASSWORD',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    users.push(newUser);
    this.saveUsers(tenantId, users);
    return this.respond(newUser, 201);
  }

  private updateUser(
    tenantId: number,
    userId: string,
    dto: UpdateTenantUserDto
  ): Observable<HttpEvent<any>> {
    const users = this.ensureUsers(tenantId);
    const index = users.findIndex((u) => u.userId === userId && !u.deletedAt);
    if (index === -1) return this.respond({ message: 'User not found' }, 404);

    users[index] = {
      ...users[index],
      ...(dto.name !== undefined ? { name: dto.name } : {}),
      ...(dto.role !== undefined ? { role: dto.role } : {}),
      updatedAt: new Date().toISOString(),
    };
    this.saveUsers(tenantId, users);
    return this.respond(users[index], 200);
  }

  private updateUserStatus(
    tenantId: number,
    userId: string,
    dto: UpdateTenantUserStatusDto
  ): Observable<HttpEvent<any>> {
    const users = this.ensureUsers(tenantId);
    const index = users.findIndex((u) => u.userId === userId && !u.deletedAt);
    if (index === -1) return this.respond({ message: 'User not found' }, 404);

    // Check if deactivating the last active admin
    if (dto.status === 'INACTIVE' && users[index].role === 'tenant_admin') {
      const activeAdmins = users.filter(
        (u) => !u.deletedAt && u.role === 'tenant_admin' && u.status === 'ACTIVE'
      );
      if (activeAdmins.length <= 1) {
        return this.respond(
          { message: 'Cannot deactivate the last active admin', code: 'LAST_ACTIVE_ADMIN' },
          422
        );
      }
    }

    users[index] = {
      ...users[index],
      status: dto.status,
      updatedAt: new Date().toISOString(),
    };
    this.saveUsers(tenantId, users);
    return this.respond(users[index], 200);
  }

  private deleteUser(tenantId: number, userId: string): Observable<HttpEvent<any>> {
    const users = this.ensureUsers(tenantId);
    const index = users.findIndex((u) => u.userId === userId && !u.deletedAt);
    if (index === -1) return this.respond({ message: 'User not found' }, 404);

    users[index] = { ...users[index], deletedAt: new Date().toISOString() };
    this.saveUsers(tenantId, users);
    return this.respond(null, 204);
  }

  private respond<T>(body: T, status: number): Observable<HttpEvent<T>> {
    return of(new HttpResponse({ status, body })).pipe(delay(400));
  }
}
